'use client'

// RegionSurfaces abstraction (Overmind→server-props migration, Phase A2.2).
//
// WHAT THIS IS
// The dedicated seam that supplies the per-region PRICED SURFACES —
// subscriptions (`vrsSubscriptions`), add-ons (`vrsAddOns`), coaching offers
// (`vrsCoachingOffers`) and the turnkey configurator (`turnkeyOptions`) — to the
// section components that render them, sourced from the SERVER-FETCHED region
// surface bundle threaded as props, NOT from a render-time Overmind
// `state.vrs*` / `state.turnkeyOptions` read. It mirrors components/RegionContent
// and components/NamedPrices exactly: a `'use client'` context Provider + a
// per-surface accessor hook (each returning the effective dataset plus an
// `ensureRequested()` that reproduces the old mount-effect refetch), so the
// section leaves that consumed those store fields (sections/PricingTable,
// sections/PricingTableCoaching, sections/AddOnsTable,
// sections/GetFaster/GetFasterPricing, sections/GetFaster/GetFasterPricingTable,
// sections/TeamVrsNascarPage, sections/TurnkeySolutions) no longer import
// Overmind for their surface data — they import this module.
//
// DATA SOURCE
// lib/regionPageData.loadRegionPageData already fetches the region surface bundle
// (getRegionSurfaceBundle -> { vrsSubscriptions, vrsAddOns, vrsCoachingOffers,
// turnkeyOptions }) for any page that renders a surface section and returns it as
// `surfaces`. Those SAME values are threaded into <RegionSurfacesProvider> in the
// App Router page island (RegionPageClient) — the identical arrays/object that
// buildSurfaceMutations still hydrates into state.vrs*/state.turnkeyOptions (see
// FALLBACK) — so the provider value === the hydrated store value, which is what
// keeps the surface render hydration-safe (SSR HTML === first client render, no
// React #418/#423). ZERO new fetch: this is a pure threading of already-loaded
// data (the surface bundle was already fetched server-side in tasks #19/#188).
//
// APP-ROUTER SAFETY
// This module is a `'use client'` boundary (it holds React context). It is
// imported ONLY by client components (the surface section leaves) — so it
// introduces no new server-component/RSC coupling. No next/router, no window at
// render.
//
// SERVER-PROPS ONLY (Overmind retirement, Phase D2)
// The transitional Overmind fallback (state.vrs* / state.turnkeyOptions + the
// vrs*Requested / turnkeyOptionsRequested fetch actions) is GONE: the hooks return
// purely the server-threaded surfaces. In production every surface section renders
// under the <RegionSurfacesProvider> RegionPageClient establishes, fed the region
// surface bundle loadRegionPageData already fetches. `ensureRequested()` is kept as
// a NO-OP so the section mount-effect callers stay untouched.

import React, { createContext, useContext } from 'react'

// Server-threaded { subscriptions, addOns, coachingOffers, turnkeyOptions }, or
// null when no provider is present.
const RegionSurfacesContext = createContext(null)

export function RegionSurfacesProvider({
  subscriptions = null,
  addOns = null,
  coachingOffers = null,
  turnkeyOptions = null,
  children,
}) {
  // A stable value object per render is fine here: the provider sits at the page
  // island root (RegionPageClient), which re-renders rarely, and the consumers
  // read plain arrays/objects (no referential-identity effects depend on it).
  return (
    <RegionSurfacesContext.Provider value={{ subscriptions, addOns, coachingOffers, turnkeyOptions }}>
      {children}
    </RegionSurfacesContext.Provider>
  )
}

/**
 * The region VRS subscription plans for the subscription/pricing sections.
 *
 * Prefers the server-threaded array when the provider carries a non-empty one
 * (the server-props path); otherwise defers to the Overmind store
 * (state.vrsSubscriptions), which is byte-identical because buildSurfaceMutations
 * hydrates it from the SAME payload. `ensureRequested()` reproduces the old mount
 * effect (`if (!vrsSubscriptions.length) actions.vrsSubscriptionsRequested()`) —
 * a no-op on the server-props path, the legacy refetch on the fallback path.
 *
 * @returns {{ subscriptions: any[], ensureRequested: () => void }}
 */
export function useRegionSubscriptions() {
  const ctx = useContext(RegionSurfacesContext)
  const serverSubscriptions = ctx ? ctx.subscriptions : null
  return {
    subscriptions: Array.isArray(serverSubscriptions) ? serverSubscriptions : [],
    ensureRequested: () => {},
  }
}

/**
 * The region VRS add-ons for the add-ons table. Server-props only.
 * @returns {{ addOns: any[], ensureRequested: () => void }}
 */
export function useRegionAddOns() {
  const ctx = useContext(RegionSurfacesContext)
  const serverAddOns = ctx ? ctx.addOns : null
  return {
    addOns: Array.isArray(serverAddOns) ? serverAddOns : [],
    ensureRequested: () => {},
  }
}

/**
 * The region VRS coaching offers for the coaching pricing table. Server-props only.
 * @returns {{ coachingOffers: any[], ensureRequested: () => void }}
 */
export function useRegionCoachingOffers() {
  const ctx = useContext(RegionSurfacesContext)
  const serverCoachingOffers = ctx ? ctx.coachingOffers : null
  return {
    coachingOffers: Array.isArray(serverCoachingOffers) ? serverCoachingOffers : [],
    ensureRequested: () => {},
  }
}

/**
 * The region turnkey configurator options (an OBJECT, not an array) for the
 * turnkey solutions section. Server-props only; null when absent.
 * @returns {{ turnkeyOptions: object|null, ensureRequested: () => void }}
 */
export function useRegionTurnkeyOptions() {
  const ctx = useContext(RegionSurfacesContext)
  const serverTurnkeyOptions = ctx ? ctx.turnkeyOptions : null
  return {
    turnkeyOptions: serverTurnkeyOptions != null ? serverTurnkeyOptions : null,
    ensureRequested: () => {},
  }
}

export { RegionSurfacesContext }
